import { ExternalLink } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import {
  useOrganizationAlertArticle,
  type OrganizationAlert,
} from '@/shared/hooks/api/regulatoryNews'
import { useGetUserSession } from '@/shared/hooks/api/auth'
import { Alert, AlertDescription } from '@/components/ui/alert'
import { Button } from '@/components/ui/button'
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetFooter,
  SheetHeader,
  SheetTitle,
} from '@/components/ui/sheet'
import { Skeleton } from '@/components/ui/skeleton'

type NewsroomAlertArticleSheetProps = {
  alert: OrganizationAlert
  open: boolean
  onOpenChange: (open: boolean) => void
}

export function NewsroomAlertArticleSheet({
  alert,
  open,
  onOpenChange,
}: NewsroomAlertArticleSheetProps) {
  const { t } = useTranslation('regulatory-alerts')
  const { data: session } = useGetUserSession()
  const organizationId = session?.organizationId

  const articleQuery = useOrganizationAlertArticle(organizationId, alert.id, {
    enabled: open,
  })

  const showLoading = articleQuery.isLoading || articleQuery.isFetching
  const showError = articleQuery.isError && !showLoading

  const articleTitle = articleQuery.data?.title?.trim() ?? ''
  const hasArticleTitle = articleTitle.length > 0
  const showHeadlineSkeleton =
    !hasArticleTitle && (articleQuery.isLoading || articleQuery.isFetching)

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent
        side='right'
        className='flex w-full flex-col gap-0 sm:max-w-xl'
      >
        <SheetHeader className='border-b'>
          <SheetTitle>{t('article.title')}</SheetTitle>
          {showHeadlineSkeleton ? (
            <Skeleton className='h-5 w-4/5' />
          ) : null}
          {!showHeadlineSkeleton && hasArticleTitle ? (
            <SheetDescription>{articleTitle}</SheetDescription>
          ) : null}
          {!showHeadlineSkeleton && !hasArticleTitle && articleQuery.data ? (
            <SheetDescription>{t('inbox.noTitle')}</SheetDescription>
          ) : null}
        </SheetHeader>

        <div className='min-h-0 flex-1 overflow-y-auto p-4'>
          {showLoading ? (
            <div className='space-y-3'>
              <p className='text-sm text-muted-foreground'>{t('article.loading')}</p>
              <Skeleton className='h-4 w-full' />
              <Skeleton className='h-4 w-full' />
              <Skeleton className='h-4 w-3/4' />
            </div>
          ) : null}

          {showError ? (
            <Alert variant='destructive'>
              <AlertDescription>{t('article.error')}</AlertDescription>
            </Alert>
          ) : null}

          {articleQuery.data && !showLoading ? (
            <div className='space-y-4'>
              {articleQuery.data.wasTranslated ? (
                <p className='text-xs text-muted-foreground'>
                  {t('article.translatedDisclaimer')}
                </p>
              ) : null}
              <div className='text-sm whitespace-pre-wrap'>
                {articleQuery.data.body}
              </div>
            </div>
          ) : null}
        </div>

        <SheetFooter className='border-t sm:flex-col sm:items-stretch sm:gap-2'>
          {showError ? (
            <Button
              type='button'
              variant='secondary'
              onClick={() => {
                void articleQuery.refetch()
              }}
            >
              {t('article.retry')}
            </Button>
          ) : null}
          {articleQuery.data?.sourceUrl ? (
            <Button asChild variant='outline'>
              <a
                href={articleQuery.data.sourceUrl}
                target='_blank'
                rel='noopener noreferrer'
              >
                {t('article.openOriginal')}
                <ExternalLink className='ms-2 size-4' />
              </a>
            </Button>
          ) : null}
        </SheetFooter>
      </SheetContent>
    </Sheet>
  )
}
